import {
	Drawer,
	type DrawerProps,
	IconButton,
	Stack,
	type SxProps,
	type Theme,
	Typography,
	useTheme,
} from "@mui/material";
import { X } from "lucide-react";
import type { FC, PropsWithChildren, ReactNode, SyntheticEvent } from "react";
import { useRef } from "react";
import {
	useGoalsRightOptional,
	usePortfolioRightOptional,
} from "web_myplann/Api/Rights/RightsProvider";
import { DrawerGrabber } from "web_myplann/Shared/Components/DrawerGrabber";
import { useHideNavbar } from "web_myplann/Shared/Hooks/useHideNavbar";
import { useSoftKeyboardOpen } from "web_myplann/Shared/Hooks/useSoftKeyboardOpen";

type TitleHeaderProps = {
	title?: string;
	closeButton?: boolean;
	header?: never;
};

type CustomHeaderProps = {
	header: ReactNode;
	title?: never;
	closeButton?: never;
};

export type MyPlannDrawerProps = DrawerProps & {
	grabber?: boolean;
	dissableBackdropClick?: boolean;
	concealed?: boolean;
	paperSx?: SxProps<Theme>;
} & (TitleHeaderProps | CustomHeaderProps);

export const MyPlannDrawer: FC<PropsWithChildren<MyPlannDrawerProps>> = ({
	anchor = "bottom",
	title,
	header,
	closeButton,
	grabber,
	dissableBackdropClick,
	concealed,
	paperSx,
	children,
	onClose,
	open,
	...rest
}) => {
	const { palette, iconSize, spacingTokens } = useTheme();
	const hasGoalsRight = useGoalsRightOptional();
	const { hasRight: hasPortfolioRight } = usePortfolioRightOptional();
	const isRedesigned = hasGoalsRight || hasPortfolioRight;

	const startYRef = useRef<number | null>(null);
	const closedRef = useRef(false);
	const threshold = 60;

	const drawerOpen = useHideNavbar(open === true);
	const isSoftKeyboardOpen = useSoftKeyboardOpen(open === true);

	const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
		startYRef.current =
			e.touches?.[0]?.clientY ?? e.targetTouches?.[0]?.clientY ?? null;
		e.stopPropagation();
	};

	const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
		if (anchor === "bottom" && startYRef.current != null) {
			const y =
				e.touches?.[0]?.clientY ?? e.targetTouches?.[0]?.clientY ?? null;
			if (
				y != null &&
				y - startYRef.current > threshold &&
				!closedRef.current
			) {
				closedRef.current = true;
				onClose?.({}, "escapeKeyDown");
			}
		}
		e.stopPropagation();
	};

	const handleTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
		startYRef.current = null;
		closedRef.current = false;
		e.stopPropagation();
	};

	return (
		<Drawer
			anchor={anchor}
			open={drawerOpen}
			ModalProps={{
				sx: {
					zIndex: (t) => t.zIndex.drawer + 1,
					visibility: concealed ? "hidden" : undefined,
				},
				slotProps: { backdrop: { onClick: (e) => e.stopPropagation() } },
			}}
			slotProps={{
				paper: {
					sx: [
						{
							paddingBottom: isSoftKeyboardOpen
								? 0
								: "min(var(--mobile-safe-area-bottom, env(safe-area-inset-bottom, 0px)), 10px)",
						},
						...(isRedesigned
							? [{ backgroundColor: palette.background.paper }]
							: []),
						...(Array.isArray(paperSx) ? paperSx : paperSx ? [paperSx] : []),
					],
					onClick: (e: SyntheticEvent) => {
						e.stopPropagation();
					},
				},
			}}
			onClose={(event, reason) => {
				if (reason === "backdropClick" && closeButton) {
					return;
				}
				if (onClose) onClose(event, reason);
			}}
			{...rest}
		>
			{grabber && (
				<DrawerGrabber
					onTouchStart={handleTouchStart}
					onTouchMove={handleTouchMove}
					onTouchEnd={handleTouchEnd}
				/>
			)}
			{header ?? (
				<Stack
					pt={grabber ? spacingTokens.xs : spacingTokens.md}
					pb={isRedesigned ? spacingTokens.xs : spacingTokens.sm}
					px={spacingTokens.lg}
					direction={"row"}
					alignItems={"center"}
					justifyContent={"space-between"}
					onTouchStart={handleTouchStart}
					onTouchMove={handleTouchMove}
					onTouchEnd={handleTouchEnd}
					sx={{ touchAction: "none" }}
				>
					<Typography
						display="flex"
						alignItems="flex-start"
						variant={isRedesigned ? "h4" : "deprecatedH3"}
					>
						{title}
					</Typography>

					{closeButton && (
						<IconButton
							onClick={(e) => {
								onClose?.(e, "escapeKeyDown");
							}}
							sx={{
								bgcolor: palette.grey[100],
							}}
						>
							<X size={iconSize.sm} color={palette.grey[500]} />
						</IconButton>
					)}
				</Stack>
			)}
			<Stack flex="1 1 auto" sx={{ overflowY: "auto" }}>
				{children}
			</Stack>
		</Drawer>
	);
};
